import angular from 'angular';
import {
    buildMovimentacaoPeriodoPresentationItem,
    formatMovimentacaoPeriodoCompetencia,
    getMovimentacaoPeriodoStatusClass,
    getMovimentacaoPeriodoStatusLabel
} from '../../helpers/movimentacoes-periodos-ui.helpers';
import {
    IMovimentacaoPeriodoForm,
    IMovimentacaoPeriodoPresentationItem
} from '../../models/movimentacoes-periodos-ui.model';
import {
    PeriodoSelectorCreateCallback,
    PeriodoSelectorFormFactory,
    PeriodoSelectorNoArgCallback,
    PeriodoSelectorSelectCallback
} from './movimentacoes-periodo-selector.model';
import { IMovimentacaoPeriodo } from '../../../models/movimentacoes-periodos.service.model';

export class MovimentacoesPeriodoSelectorController implements angular.IController {
    static $inject = ['$document', '$element'];

    public periodos: IMovimentacaoPeriodo[] = [];
    public selectedPeriod: IMovimentacaoPeriodo | null = null;
    public periodosBusy = false;
    public novoPeriodoBusy = false;
    public canManage = false;
    public buildCreateForm: PeriodoSelectorFormFactory;
    public onSelect: PeriodoSelectorSelectCallback;
    public onCreate: PeriodoSelectorCreateCallback;
    public onOpenAll: PeriodoSelectorNoArgCallback;
    public isOpen = false;
    public isCreateOpen = false;
    public createModel: IMovimentacaoPeriodoForm = this.createEmptyForm();
    public presentationItems: IMovimentacaoPeriodoPresentationItem[] = [];
    private interactionStartedInside = false;

    constructor(
        private $document: angular.IDocumentService,
        private $element: angular.IAugmentedJQuery
    ) {}

    public $onInit(): void {
        this.$document.on('mousedown', this.onDocumentMouseDown);
        this.$document.on('click', this.onDocumentClick);
        this.$document.on('keydown', this.onDocumentKeydown);
        this.refreshPresentationItems();
    }

    public $onChanges(changesObj: angular.IOnChangesObject): void {
        if (changesObj.periodos || changesObj.selectedPeriod) {
            this.refreshPresentationItems();
        }
    }

    public $onDestroy(): void {
        this.$document.off('mousedown', this.onDocumentMouseDown);
        this.$document.off('click', this.onDocumentClick);
        this.$document.off('keydown', this.onDocumentKeydown);
    }

    public toggleSelector(event?: Event): void {
        this.stopEvent(event);

        this.isOpen = !this.isOpen;

        if (this.isOpen && !this.periodosBusy && !this.periodos.length) {
            this.openCreate();
        }
    }

    public close(): void {
        this.isOpen = false;
        this.interactionStartedInside = false;
        this.cancelCreate();
    }

    public submitCreate(payload: IMovimentacaoPeriodoForm): void {
        if (!this.canManage || this.novoPeriodoBusy || !this.onCreate) {
            return;
        }

        const result = this.onCreate({ payload: angular.copy(payload) });

        if (result && typeof (result as angular.IPromise<unknown>).then === 'function') {
            (result as angular.IPromise<unknown>).then(() => {
                this.close();
            }).catch(() => undefined);
        } else {
            this.close();
        }
    }

    public selectPeriodo(periodo: IMovimentacaoPeriodo, event?: Event): void {
        this.stopEvent(event);

        if (!periodo || !this.onSelect) {
            return;
        }

        const result = this.onSelect({ periodo });

        if (result && typeof (result as angular.IPromise<unknown>).then === 'function') {
            (result as angular.IPromise<unknown>).then(() => {
                this.close();
            }).catch(() => undefined);
            return;
        }

        this.close();
    }

    public openCreate(event?: Event): void {
        if (!this.canManage) {
            return;
        }

        this.stopEvent(event);
        this.isOpen = true;
        this.isCreateOpen = true;
        this.createModel = angular.copy(this.buildDefaultCreateForm());
    }

    public cancelCreate(event?: Event): void {
        this.stopEvent(event);
        this.isCreateOpen = false;
        this.createModel = this.createEmptyForm();
    }

    public openAll(event?: Event): void {
        if (!this.canManage) {
            return;
        }

        this.stopEvent(event);

        if (this.onOpenAll) {
            this.onOpenAll();
        }

        this.close();
    }

    public getCurrentTitle(): string {
        if (!this.selectedPeriod) {
            return 'Nenhum período criado';
        }

        return formatMovimentacaoPeriodoCompetencia(this.selectedPeriod);
    }

    public getCurrentStatusLabel(): string {
        if (!this.selectedPeriod) {
            return '';
        }

        return getMovimentacaoPeriodoStatusLabel(this.selectedPeriod.situacao);
    }

    public getCurrentStatusClass(): string {
        if (!this.selectedPeriod) {
            return 'label-default';
        }

        return getMovimentacaoPeriodoStatusClass(this.selectedPeriod.situacao);
    }

    public getAvailableCountLabel(): string {
        const total = this.presentationItems.length;
        return total === 1 ? '1 disponível' : `${total} disponíveis`;
    }

    private refreshPresentationItems(): void {
        this.presentationItems = (this.periodos || []).map((periodo) =>
            buildMovimentacaoPeriodoPresentationItem(periodo, this.selectedPeriod, { includeFechamento: true })
        );
    }

    private readonly onDocumentMouseDown = (event: JQueryEventObject): void => {
        if (!this.isOpen) {
            this.interactionStartedInside = false;
            return;
        }

        const hostElement = this.$element[0];
        const startedInsideHost = !!hostElement && hostElement.contains(event.target as Node);

        this.interactionStartedInside = startedInsideHost || this.isDatePickerInteraction(event.target);
    }

    private readonly onDocumentClick = (event: JQueryEventObject): void => {
        if (!this.isOpen) {
            return;
        }

        if (this.interactionStartedInside) {
            this.interactionStartedInside = false;
            return;
        }

        if (this.isDatePickerInteraction(event.target)) {
            return;
        }

        const hostElement = this.$element[0];
        if (hostElement && !hostElement.contains(event.target as Node)) {
            this.$element.scope()?.$applyAsync(() => {
                this.close();
            });
        }
    }

    private readonly onDocumentKeydown = (event: JQueryEventObject): void => {
        const isEscape = event.key === 'Escape' || event.which === 27;

        if (isEscape && this.isOpen) {
            this.$element.scope()?.$applyAsync(() => {
                this.close();
            });
        }
    }

    private buildDefaultCreateForm(): IMovimentacaoPeriodoForm {
        if (this.buildCreateForm) {
            return this.buildCreateForm();
        }

        return this.createEmptyForm();
    }

    private createEmptyForm(): IMovimentacaoPeriodoForm {
        const currentDate = new Date();

        return {
            mes: currentDate.getMonth() + 1,
            ano: currentDate.getFullYear(),
            encerramentolancamentos: ''
        };
    }

    private stopEvent(event?: Event): void {
        if (!event) {
            return;
        }

        if (typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        if (typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }
    }

    private isDatePickerInteraction(target: EventTarget | null): boolean {
        if (!target) {
            return false;
        }

        const element = target as Element;
        if (!element || typeof element.closest !== 'function') {
            return false;
        }

        return !!element.closest(
            '.nsj-date-picker, .nsj-calendar, .uib-datepicker-popup, .uib-daypicker, .datepicker, .bootstrap-datetimepicker-widget'
        );
    }
}
